import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Shield } from "lucide-react";
import styles from "./cisoDashboard.module.scss";
import FilterableTable from "../../shared/Tables/FilterableTable";

const assets = [
  {
    name: "SCADA-CTRL-01",
    type: "Controller",
    risk: 87,
    action: "Session Terminated",
    time: "15m ago",
    status: "Protected",
  },
  {
    name: "PLC-PROD-12",
    type: "PLC",
    risk: 72,
    action: "Access Denied",
    time: "1h ago",
    status: "Protected",
  },
  {
    name: "HMI-FLOOR-3",
    type: "HMI",
    risk: 68,
    action: "Traffic Filtered",
    time: "2h ago",
    status: "Monitoring",
  },
  {
    name: "RTU-PUMP-08",
    type: "RTU",
    risk: 55,
    action: "Policy Enforced",
    time: "4h ago",
    status: "Protected",
  },
  {
    name: "IoT-SENSOR-247",
    type: "Sensor",
    risk: 51,
    action: "Quarantined",
    time: "6h ago",
    status: "At Risk",
  },
];

const riskColor = (risk) => {
  if (risk > 80) return "#ef4444";
  if (risk > 60) return "#f59e0b";
  return "#10b981";
};

const statusColor = (status) => {
  if (status === "At Risk") return "#ef4444";
  if (status === "Monitoring") return "#f59e0b";
  return "#10b981";
};

export const CriticalAssetsTable = ({ handleNavigate }) => {
  const categories = React.useMemo(
    () => ["all", ...new Set(assets.map((asset) => asset.type))],
    []
  );
  const statuses = React.useMemo(
    () => ["all", ...new Set(assets.map((asset) => asset.status))],
    []
  );

  return (
    <FilterableTable
      title="Critical OT Assets"
      data={assets}
      columns={[
        {
          id: "name",
          label: "Asset Name",
          renderCell: (asset) => asset.name,
        },
        {
          id: "type",
          label: "Category",
          renderCell: (asset) => asset.type,
        },
        {
          id: "risk",
          label: "Risk Score",
          renderCell: (asset) => (
            <Box display="flex" alignItems="center" gap={1}>
              <Box className={styles.progressBar} style={{ maxWidth: 100 }}>
                <Box
                  className={styles.progressFill}
                  style={{
                    width: `${asset.risk}%`,
                    background: riskColor(asset.risk),
                  }}
                />
              </Box>
              <Typography fontWeight={700}>{asset.risk}</Typography>
            </Box>
          ),
        },
        {
          id: "action",
          label: "Last Enforcement Action",
          renderCell: (asset) => (
            <Chip
              size="small"
              label={asset.action}
              sx={{
                bgcolor: "#eef2f7",
                color: "#111827",
                fontWeight: 600,
              }}
            />
          ),
        },
        {
          id: "time",
          label: "Time",
          renderCell: (asset) => asset.time,
        },
        {
          id: "status",
          label: "Status",
          renderCell: (asset) => (
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              color={statusColor(asset.status)}
            >
              <Shield size={16} />
              <Typography fontWeight={700}>{asset.status}</Typography>
            </Box>
          ),
        },
      ]}
      searchKeys={["name"]}
      searchPlaceholder="Search by Asset Name"
      filters={[
        {
          id: "riskBucket",
          label: "Select Risk",
          options: ["all", "high", "medium", "low"],
          filterFn: (row, selected) => {
            if (selected === "high") return row.risk >= 80;
            if (selected === "medium") return row.risk >= 60 && row.risk < 80;
            if (selected === "low") return row.risk < 60;
            return true;
          },
          formatOption: (option) => {
            if (option === "all") return "All";
            if (option === "high") return "High (80+)";
            if (option === "medium") return "Medium (60-79)";
            return "Low (<60)";
          },
          selectSx: { minWidth: 180 },
        },
        {
          id: "type",
          label: "Category",
          options: categories,
          valueAccessor: (row) => row.type,
          formatOption: (option) =>
            option === "all" ? "All Categories" : option,
          selectSx: { minWidth: 180 },
        },
        {
          id: "status",
          label: "Status",
          options: statuses,
          valueAccessor: (row) => row.status,
          formatOption: (option) =>
            option === "all" ? "All Statuses" : option,
          selectSx: { minWidth: 180 },
        },
      ]}
      emptyMessage="No assets match the current filters."
      rowsPerPageOptions={[5, 10, 25]}
      classNames={{
        card: styles.card,
        tableHeader: styles.tableHeader,
        tableHeaderActions: styles.tableHeaderActions,
        sectionTitle: styles.sectionHeading,
        searchField: styles.searchField,
        filterToggle: styles.filterToggle,
        filterRow: styles.filterRow,
        filterGroup: styles.filterGroup,
        filterLabel: styles.filterLabel,
        filterSelect: styles.filterSelect,
        clearFilters: styles.clearFilters,
        tableContainer: styles.tableWrap,
        tableHead: styles.tableHead,
        tablePagination: styles.tablePagination,
        emptyState: styles.emptyState,
      }}
      onRowClick={() => handleNavigate("critical-ot-assets")}
      showFilterToggle
      getRowKey={(row) => row.name}
    />
  );
};

export default CriticalAssetsTable;
